import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/components/ui/helpers/toast';
import { useShallow } from 'zustand/react/shallow';

import { ComponentBoundary } from '@/features/team/components/component-boundary';
import { Skeleton } from '@/components/ui/skeleton';
import { useBulkInviteUsers } from '@/features/team/hooks/use-bulk-invite-users';
import { useRemoveTeamMember } from '@/features/team/hooks/use-remove-team-member';
import { useResendInvite } from '@/features/team/hooks/use-resend-invite';
import { useTeamMembers } from '@/features/team/hooks/use-team-members';
import { useUpdateTeamMemberRole } from '@/features/team/hooks/use-update-team-member-role';
import InviteMemberDialog from '@/features/team/components/invite-member-dialog';
import type { InviteFormValues, TeamMember } from '@/features/team/types/team-types';
import { TEAM_MEMBER_STATUS } from '@/features/team/types/team-enums';
import { useAccountActions, useAccountState } from '@/contexts/account/account-context';
import { useInviteModalStore } from '@/features/team/stores/use-invite-modal-store';

export default function InviteTeamMemberReminderModal() {
  const [open, setOpen] = useState(false);
  const resendInviteMutation = useResendInvite();
  const bulkInviteMutation = useBulkInviteUsers();
  const removeTeamMemberMutation = useRemoveTeamMember();
  const updateTeamMemberRoleMutation = useUpdateTeamMemberRole();
  const { currentAccount } = useAccountState();
  const { getUserRolesForAccount } = useAccountActions();
  const { hasShownModal, markModalAsShown } = useInviteModalStore(useShallow(s => ({ hasShownModal: s.hasShownModal, markModalAsShown: s.markModalAsShown })));
  const accountId = currentAccount?.id ?? null;
  const currentUserRole = currentAccount ? getUserRolesForAccount(currentAccount.id) : null;

  const isEligibleForModalCheck = useMemo(() => {
    if (!accountId || !currentUserRole) return false;
    if (!['owner', 'admin'].includes(currentUserRole)) return false;
    if (hasShownModal(accountId)) return false;

    const subscription = currentAccount?.subscription;
    if (!subscription || subscription.status !== 'active') return false;

    return true;
  }, [accountId, currentUserRole, hasShownModal, currentAccount?.subscription]);

  const { teamMembers, isLoading, isError, error } = useTeamMembers(accountId ?? undefined, currentUserRole, isEligibleForModalCheck);

  // Handler-only optimistic cache — dialog renders `teamMembers` from the query, not this list
  const displayedMembersRef = useRef<TeamMember[]>([]);

  const handleInviteSubmit = (data: InviteFormValues) => {
    if (accountId) {
      // Convert all email addresses to lowercase
      const invitations = data.invitations.map(invite => ({
        ...invite,
        email: invite.email.toLowerCase(),
      }));

      if (invitations.length === 1) {
        const { email, role } = invitations[0];
        resendInviteMutation.mutate(
          { accountId, email, role },
          {
            onSuccess: () => {
              setOpen(false);
              // add optimistic update for single invite
              const newPendingMember: TeamMember = {
                id: `pending-${Date.now()}-0`,
                name: 'Invited User',
                email: email,
                status: TEAM_MEMBER_STATUS.PENDING,
                role: role as TeamMember['role'],
              };
              displayedMembersRef.current = [...displayedMembersRef.current, newPendingMember];
            },
            onError: () => {
              toast.error('Failed to send invitation. Please try again.');
            },
          }
        );
      } else if (invitations.length > 1) {
        const emails = invitations.map(invite => invite.email);
        const roles = invitations.map(invite => invite.role);

        if (emails.length > 10) {
          toast.error('Maximum 10 invitations can be sent at once.');
          return;
        }

        bulkInviteMutation.mutate(
          { accountId, emails, roles },
          {
            onSuccess: () => {
              setOpen(false);
              const newPendingMembers: TeamMember[] = invitations.map((invite, index) => ({
                id: `pending-${Date.now()}-${index}`,
                name: 'Invited User',
                email: invite.email,
                status: TEAM_MEMBER_STATUS.PENDING,
                role: invite.role as TeamMember['role'],
              }));
              displayedMembersRef.current = [...displayedMembersRef.current, ...newPendingMembers];
            },
            onError: () => {
              toast.error('Failed to send invitations. Please try again.');
            },
          }
        );
      } else {
        toast.error('At least one invitation is required.');
      }
    } else {
      toast.error('No account selected. Please select an account first.');
    }
  };

  const handleRoleChange = (memberId: string | number, newRole: TeamMember['role']) => {
    const memberToUpdate = displayedMembersRef.current.find(m => m.id === memberId);

    if (memberToUpdate && accountId) {
      displayedMembersRef.current = displayedMembersRef.current.map(m =>
        m.id === memberId ? { ...m, role: newRole } : m
      );

      updateTeamMemberRoleMutation.mutate(
        {
          accountId,
          userId: String(memberId),
          role: newRole,
        },
        {
          onError: () => {
            displayedMembersRef.current = displayedMembersRef.current.map(m =>
              m.id === memberId ? { ...m, role: memberToUpdate.role } : m
            );
          },
        }
      );
    }
  };

  const handleResendInvite = (memberId: string | number) => {
    const memberToResend = displayedMembersRef.current.find(m => m.id === memberId);

    if (memberToResend && accountId) {
      resendInviteMutation.mutate({
        accountId,
        email: memberToResend.email.toLowerCase(),
        role: memberToResend.role,
      });
    }
  };

  const handleCancelInvite = (memberId: string | number) => {
    if (!accountId) return;

    const memberToCancel = displayedMembersRef.current.find(m => m.id === memberId);

    if (memberToCancel) {
      displayedMembersRef.current = displayedMembersRef.current.filter(m => m.id !== memberId);

      removeTeamMemberMutation.mutate(
        {
          accountId,
          userId: String(memberId),
        },
        {
          onSuccess: () => {
            toast.success('Invitation cancelled successfully.');
          },
          onError: () => {
            displayedMembersRef.current = [...displayedMembersRef.current, memberToCancel];
            toast.error('Failed to cancel invitation. Please try again.');
          },
        }
      );
    }
  };

  const handleDeleteMember = (memberId: string | number) => {
    if (!accountId) return;

    const memberToDelete = displayedMembersRef.current.find(m => m.id === memberId);

    if (memberToDelete) {
      displayedMembersRef.current = displayedMembersRef.current.filter(m => m.id !== memberId);

      removeTeamMemberMutation.mutate(
        {
          accountId,
          userId: String(memberId),
        },
        {
          onSuccess: () => {
            toast.success('Team member removed successfully.');
          },
          onError: () => {
            displayedMembersRef.current = [...displayedMembersRef.current, memberToDelete];
            toast.error('Failed to remove team member. Please try again.');
          },
        }
      );
    }
  };

  // Disabled during signup flow redesign. Original gate (for re-enablement):
  // !isEligibleForModalCheck || isLoading → false
  // activeTeamMembers.length > 2 → false
  // hasPendingMembers → false
  // else true
  const canShowModal = false;

  useEffect(() => {
    if (canShowModal && accountId) {
      setOpen(true);
      markModalAsShown(accountId);
    }
  }, [canShowModal, accountId, markModalAsShown]);

  return (
    <InviteMemberDialog
      open={open}
      onOpenChange={setOpen}
      onInviteSubmit={handleInviteSubmit}
      isSubmitting={bulkInviteMutation.isLoading || resendInviteMutation.isLoading}
      currentUserRole={currentUserRole}
      teamMembers={teamMembers}
      showPendingMembers={true}
      onRoleChange={handleRoleChange}
      onResendInvite={handleResendInvite}
      onCancelInvite={handleCancelInvite}
      onDeleteMember={handleDeleteMember}
      isLoading={isLoading || removeTeamMemberMutation.isLoading}
      isError={isError}
      error={error as Error}
    />
  );
}

const InviteTeamMemberModalContainer = () => {
  return (
    <ComponentBoundary
      errorTitle="Failed to load invite modal"
      errorDescription="There was an error loading the team invitation modal. Please try again."
      loadingFallback={
        <div className="flex h-40 w-80 items-center justify-center rounded-lg border bg-background p-6">
          <div className="flex flex-col items-center gap-3">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <div className="flex gap-2">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-16" />
            </div>
          </div>
        </div>
      }
      retryable={true}
    >
      <InviteTeamMemberReminderModal />
    </ComponentBoundary>
  );
};

export { InviteTeamMemberModalContainer };
